import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { collection, orderBy, query, where } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Drawer from "@mui/material/Drawer";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Bell, Eye } from "lucide-react";
import type { Session } from "@event-planner/shared";
import { AudiencePicker } from "../../components/AudiencePicker";
import { EmailPreview } from "../../components/previews/EmailPreview";
import { FormPageLayout } from "../../components/forms/FormPageLayout";
import { FormSection } from "../../components/forms/FormSection";
import { SchemaField, type FieldConfig } from "../../components/forms/SchemaField";
import { db } from "../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { useCreateNotification } from "../../hooks/useNotificationMutations";
import { useEvent } from "../../layouts/EventLayout";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { useIsPhone } from "../../hooks/useIsPhone";
import {
  notificationSchema,
  type NotificationFormData,
} from "../../schemas/notificationSchema";
import { buildDeepLinkOptions } from "../../utils/deepLinks";

const DEFAULT_VALUES: NotificationFormData = {
  title: "",
  body: "",
  channels: ["push"],
  emailSubject: null,
  pickedAudienceIds: [],
  scheduleMode: "now",
  scheduledAt: null,
  deepLink: null,
};

export function NotificationComposePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event } = useEvent();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: creating } = useCreateNotification();
  const [serverError, setServerError] = useState<string | null>(null);
  const isPhone = useIsPhone();
  const [previewOpen, setPreviewOpen] = useState(false);

  const timezone = event?.timezone ?? "UTC";

  // Fetch sessions for this event — used for the deep link dropdown.
  const sessionsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "sessions"),
            where("eventId", "==", eventId),
            orderBy("date", "asc"),
            orderBy("startTime", "asc"),
          )
        : null,
    [eventId],
  );
  const { data: sessions } = useFirestoreCollectionOnce<Session>(sessionsQuery);

  const form = useForm<NotificationFormData>({
    resolver: zodResolver(notificationSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting || creating,
  );

  const channels = form.watch("channels");
  const scheduleMode = form.watch("scheduleMode");
  const title = form.watch("title");
  const body = form.watch("body");
  const deepLink = form.watch("deepLink");

  const deepLinkOptions = useMemo(
    () =>
      eventId
        ? buildDeepLinkOptions(
            eventId,
            sessions.map((s) => ({ id: s.id, title: s.title })),
          )
        : [],
    [eventId, sessions],
  );
  const contentFields: FieldConfig<NotificationFormData>[] = [
    {
      name: "title",
      label: "Title",
      type: "text",
      required: true,
      helperText: "Shown as the push notification headline and email subject fallback",
    },
    {
      name: "body",
      label: "Message",
      type: "textarea",
      required: true,
      rows: 4,
      helperText: "Shown as the push body and email content (plain text, max 500 chars)",
    },
  ];
  const scheduledAtField: FieldConfig<NotificationFormData> = {
    name: "scheduledAt",
    label: `Scheduled send time (${timezone})`,
    type: "datetime-local",
    required: true,
    helperText:
      "The scheduler checks every minute, so expect up to 60 seconds of delay",
  };

  async function onSubmit(data: NotificationFormData) {
    setServerError(null);
    try {
      const newId = await create({
        eventId: eventId!,
        title: data.title,
        body: data.body,
        channels: data.channels,
        emailSubject: data.emailSubject,
        pickedAudienceIds: data.pickedAudienceIds,
        scheduleMode: data.scheduleMode,
        scheduledAt: data.scheduledAt,
        timezone,
        deepLink: data.deepLink && data.deepLink.length > 0 ? data.deepLink : null,
      });
      showSuccess(
        data.scheduleMode === "now"
          ? "Notification sent"
          : "Notification scheduled",
      );
      // For immediate sends, land on the detail page so the user sees the
      // CF fan-out stats populate live. The detail page polls for the CF
      // update for up to 8 seconds. For scheduled sends, go back to the
      // list — there are no stats to show until the cron fires later.
      if (data.scheduleMode === "now") {
        navigate(`/events/${eventId}/notifications/${newId}`);
      } else {
        navigate(`/events/${eventId}/notifications`);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create notification";
      setServerError(message);
      showError(message);
    }
  }

  // Lock-screen and email previews. Desktop shows them in a side column;
  // phones open them in a bottom sheet from the Preview button, so the
  // preview stays one tap away while writing.
  const previewContent = (
    <>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Preview
      </Typography>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: "background.default",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Bell size={20} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {event?.name ?? "Flat Rock Events"}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {title || "Notification title"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
              }}
            >
              {body || "Your message body will appear here."}
            </Typography>
          </Box>
        </Stack>
      </Paper>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1.5, textAlign: "center" }}
      >
        Approximate lock-screen appearance
      </Typography>

      {channels.includes("email") && (
        <EmailPreview
          eventName={event?.name ?? "Event"}
          title={title}
          body={body}
          deepLink={deepLink ?? null}
        />
      )}
    </>
  );

  return (
    <>
      {unsavedGuardDialog}
      <FormPageLayout
      title="Compose Notification"
      onSubmit={form.handleSubmit(onSubmit)}
      error={serverError}
      submitting={creating}
      submitLabel={scheduleMode === "now" ? "Send Now" : "Schedule"}
      testIdSlug="notification"
      phoneBarAction={
        <Button
          variant="outlined"
          startIcon={<Eye size={18} />}
          onClick={() => setPreviewOpen(true)}
          data-testid="notification-preview-open"
        >
          Preview
        </Button>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
          gap: 4,
        }}
      >
        {/* Left column: form fields */}
        <Box>
          <FormSection title="Content">
            {contentFields.map((field) => (
              <SchemaField
                key={field.name}
                config={field}
                control={form.control}
              />
            ))}
          </FormSection>

          <FormSection title="Delivery">
            <Controller
              name="channels"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} component="fieldset">
                  <FormLabel component="legend" required>
                    Channels
                  </FormLabel>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value.includes("push")}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...field.value, "push"]
                              : field.value.filter((c) => c !== "push");
                            field.onChange(next);
                          }}
                        />
                      }
                      label="Push notification"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value.includes("email")}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...field.value, "email"]
                              : field.value.filter((c) => c !== "email");
                            field.onChange(next);
                          }}
                        />
                      }
                      label="Email"
                    />
                  </FormGroup>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />

            {channels.includes("email") && (
              <Controller
                name="emailSubject"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    label="Email subject"
                    required
                    fullWidth
                    error={!!error}
                    helperText={
                      error?.message ??
                      "Shown in the recipient's inbox. Usually matches the push title."
                    }
                  />
                )}
              />
            )}
          </FormSection>

          <FormSection title="Recipients">
            <Controller
              name="pickedAudienceIds"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <AudiencePicker
                  eventId={eventId}
                  value={field.value}
                  onChange={field.onChange}
                  label="Recipients"
                  placeholder="Pick groups or specific people…"
                  includePendingContacts
                  required
                  helperText={
                    error?.message ??
                    "Recipients are resolved from the selected groups and people at send time."
                  }
                  testId="notification-audience"
                />
              )}
            />
          </FormSection>

          <FormSection title="Timing">
            <Controller
              name="scheduleMode"
              control={form.control}
              render={({ field }) => (
                <ToggleButtonGroup
                  value={field.value}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      field.onChange(value);
                      if (value === "now") {
                        form.setValue("scheduledAt", null);
                      }
                    }
                  }}
                  aria-label="Timing"
                  color="primary"
                >
                  <ToggleButton value="now">Send now</ToggleButton>
                  <ToggleButton value="later">Schedule for later</ToggleButton>
                </ToggleButtonGroup>
              )}
            />

            {scheduleMode === "later" && (
              <SchemaField
                config={scheduledAtField}
                control={form.control}
              />
            )}
          </FormSection>

          <FormSection title="Deep Link">
            <Controller
              name="deepLink"
              control={form.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  select
                  label="Open when tapped"
                  fullWidth
                  helperText="Where the notification takes the user when they tap it"
                >
                  {deepLinkOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </FormSection>
        </Box>

        {/* Right column: preview (phones open it as a sheet instead) */}
        {!isPhone && <Box>{previewContent}</Box>}
      </Box>
    </FormPageLayout>
    {isPhone && (
      <Drawer
        anchor="bottom"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              px: 2,
              pt: 2,
              pb: 3,
              maxHeight: "85vh",
            },
          },
        }}
      >
        {previewContent}
        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => setPreviewOpen(false)}
        >
          Back to editing
        </Button>
      </Drawer>
    )}
    </>
  );
}
